import React, { useEffect, useState } from 'react';
import { Table, Button, Spin, Alert, Space, Tag } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelFarmaciasDuplicadasCU2,
  crmFarmaciaUrl,
  type FarmaciaDuplicadaCU2,
} from '../../api/vinculacionApi';
import { exportCsv, cmpNum, cmpTxt } from './helpers';

const VinculacionFarmaciasDuplicadasTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<FarmaciaDuplicadaCU2[]>([]);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setRows(await getPanelFarmaciasDuplicadasCU2());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las farmacias duplicadas"
        action={<Button onClick={load}>Reintentar</Button>}
      />
    );
  }

  const columns: ColumnsType<FarmaciaDuplicadaCU2> = [
    {
      title: 'ID CU2',
      dataIndex: 'idFarmacia',
      align: 'right',
      width: 90,
      sorter: (a, b) => cmpNum(a.idFarmacia, b.idFarmacia),
    },
    {
      title: 'ID Gescom',
      dataIndex: 'idFarmaciaGs',
      align: 'right',
      width: 110,
      sorter: (a, b) => cmpNum(a.idFarmaciaGs, b.idFarmaciaGs),
      render: (v: number | undefined) =>
        v ? (
          <a href={crmFarmaciaUrl(v)} target="_blank" rel="noreferrer" title="Abrir ficha en CRM">
            {v}
          </a>
        ) : (
          <Tag color="orange">Sin vincular</Tag>
        ),
    },
    {
      title: 'Farmacia',
      dataIndex: 'nombreFarmacia',
      sorter: (a, b) => cmpTxt(a.nombreFarmacia, b.nombreFarmacia),
      ellipsis: true,
    },
    {
      title: 'Dirección',
      dataIndex: 'direccion',
      sorter: (a, b) => cmpTxt(a.direccion, b.direccion),
      ellipsis: true,
    },
    {
      title: 'CP',
      dataIndex: 'cp',
      width: 70,
      sorter: (a, b) => cmpTxt(a.cp, b.cp),
    },
    {
      title: 'Localidad',
      dataIndex: 'localidad',
      sorter: (a, b) => cmpTxt(a.localidad, b.localidad),
      ellipsis: true,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      sorter: (a, b) => cmpTxt(a.provincia, b.provincia),
    },
  ];

  const handleExport = () => {
    exportCsv(
      'farmacias_duplicadas_cu2.csv',
      ['ID CU2', 'ID Gescom', 'Farmacia', 'Dirección', 'CP', 'Localidad', 'Provincia'],
      rows.map((r) => [
        r.idFarmacia,
        r.idFarmaciaGs ?? '',
        r.nombreFarmacia ?? '',
        r.direccion ?? '',
        r.cp ?? '',
        r.localidad ?? '',
        r.provincia ?? '',
      ]),
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span>
          <strong>{rows.length}</strong> registros con dirección + CP duplicados
        </span>
        <Space>
          <Button icon={<DownloadOutlined />} size="small" onClick={handleExport}>
            CSV
          </Button>
          <Button icon={<ReloadOutlined />} size="small" onClick={load}>
            Recargar
          </Button>
        </Space>
      </div>

      <Table<FarmaciaDuplicadaCU2>
        dataSource={rows}
        columns={columns}
        rowKey="idFarmacia"
        size="small"
        pagination={{ pageSize: 100, showSizeChanger: true, showTotal: (t) => `${t} registros` }}
      />
    </div>
  );
};

export default VinculacionFarmaciasDuplicadasTab;
